"use client"

import type { FullTranslation } from "../../lib/i18n"
import { useTr } from "../../lib/use-tr"
import { Reveal } from "../reveal"
import { Check, ShieldCheck, Sparkles } from "lucide-react"
import Image from "next/image"

const COMPLIANCE_T = {
  eyebrow: { FR: "Conformité", EN: "Compliance" },
  headline: {
    FR: "Conforme RGPD par construction.",
    EN: "GDPR compliant by design.",
  },
  body: {
    FR: "Données issues de sources publiques uniquement, conformité CNIL native. Chaque prospect est sourcé, traçable, et supprimable en un clic.",
    EN: "Data from public sources only, native CNIL compliance. Every prospect is sourced, traceable, and removable in one click.",
  },

  b1: {
    FR: "Données publiques uniquement (sites pros, annuaires officiels, Maps)",
    EN: "Public data only (business websites, official directories, Maps)",
  },
  b2: {
    FR: "Intérêt légitime art. 6.1.f du RGPD",
    EN: "Legitimate interest, GDPR art. 6.1.f",
  },
  b3: {
    FR: "Aucune donnée personnelle B2C",
    EN: "No B2C personal data",
  },
  b4: {
    FR: "Opt-out et suppression sous 48h",
    EN: "Opt-out and deletion within 48h",
  },
  b5: {
    FR: "Hébergement EU (Hetzner)",
    EN: "EU hosting (Hetzner)",
  },

  badge_rgpd_label: { FR: "Conforme", EN: "Compliant" },
  badge_eu_label: { FR: "Hébergement EU", EN: "Hosted in EU" },
  badge_cnil_label: { FR: "Référentiel CNIL", EN: "CNIL framework" },
} satisfies FullTranslation

const BULLETS = ["b1", "b2", "b3", "b4", "b5"] as const

const ORBIT_DURATION_S = 28

const ORBITS = [
  {
    key: "rgpd",
    logo: "/autoleads/logos/rgpd.svg",
    labelKey: "badge_rgpd_label",
    inset: 0,
  },
  {
    key: "eu",
    logo: "/autoleads/logos/eu.svg",
    labelKey: "badge_eu_label",
    inset: 8,
  },
  {
    key: "cnil",
    logo: "/autoleads/logos/cnil.svg",
    labelKey: "badge_cnil_label",
    inset: 4,
  },
] as const

export function Compliance() {
  const tr = useTr(COMPLIANCE_T)
  return (
    <section
      id="compliance"
      aria-labelledby="compliance-heading"
      className="scroll-mt-28 py-16 sm:py-20 md:py-24 lg:scroll-mt-40"
    >
      <Reveal className="sm:container">
        <div className="mx-auto grid max-w-6xl items-center gap-12 md:grid-cols-2 md:gap-16">
          <div>
            <div className="bg-card/60 text-muted-foreground mb-6 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-sm">
              <Sparkles className="text-primary-cta size-3.5" aria-hidden />
              <span>{tr("eyebrow")}</span>
            </div>
            <h2
              id="compliance-heading"
              className="font-display text-foreground leading-display tracking-dense text-3xl font-semibold text-balance sm:text-4xl md:text-5xl"
            >
              {tr("headline")}
            </h2>
            <p className="text-muted-foreground text-md mt-5 max-w-xl leading-relaxed text-balance sm:text-base">
              {tr("body")}
            </p>

            <ul className="mt-8 flex flex-col gap-3">
              {BULLETS.map((key) => (
                <li key={key} className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="bg-primary-cta/10 text-primary-cta mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full"
                  >
                    <Check className="size-3" />
                  </span>
                  <span className="text-foreground text-sm leading-relaxed">
                    {tr(key)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative mx-auto flex w-full max-w-md items-center justify-center md:max-w-none">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 -z-10 [mask-image:radial-gradient(circle_at_center,black_40%,transparent_75%)]"
              style={{
                backgroundImage:
                  "radial-gradient(circle at center, oklch(0.88 0.09 50 / 0.25), transparent 65%)",
              }}
            />

            <div className="relative flex aspect-square w-full max-w-64 items-center justify-center sm:max-w-sm">
              <div
                aria-hidden
                className="border-border/60 absolute inset-0 rounded-full border"
              />
              <div
                aria-hidden
                className="border-border/50 absolute inset-8 rounded-full border"
              />
              <div
                aria-hidden
                className="border-border/40 absolute inset-16 rounded-full border"
              />

              <div className="bg-primary-cta/10 ring-primary-cta/15 relative flex size-20 items-center justify-center rounded-full ring-8 sm:size-28">
                <ShieldCheck
                  className="text-primary-cta size-9 sm:size-12"
                  aria-hidden
                />
              </div>

              {ORBITS.map((o, i) => (
                <div
                  key={o.key}
                  aria-hidden
                  className="orbit-spin pointer-events-none absolute"
                  style={{
                    inset: `${o.inset}%`,
                    animationDelay: `${i * -(ORBIT_DURATION_S / ORBITS.length)}s`,
                    animationDuration: `${ORBIT_DURATION_S}s`,
                  }}
                >
                  <div
                    className="orbit-counter absolute top-0 left-1/2"
                    style={{
                      animationDelay: `${i * -(ORBIT_DURATION_S / ORBITS.length)}s`,
                      animationDuration: `${ORBIT_DURATION_S}s`,
                    }}
                  >
                    <div className="bg-card ring-border/60 shadow-soft pointer-events-auto flex items-center rounded-lg border p-1.5 ring-1 sm:gap-2.5 sm:px-3 sm:py-2">
                      <span className="ring-border/50 relative inline-flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white ring-1">
                        <Image
                          src={o.logo}
                          alt=""
                          fill
                          className="object-contain p-1"
                        />
                      </span>
                      <span className="text-foreground hidden text-xs font-medium whitespace-nowrap sm:inline">
                        {tr(o.labelKey)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  )
}
